"use client"
import { useEffect } from "react"
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes"
import type { ThemeProviderProps } from "next-themes"
import { usePathname } from "next/navigation"
import { temaForcadoPorRota } from "@/lib/tema/politica-de-tema"
import { COR_DA_BARRA } from "@/lib/tema/cor-da-barra"

/**
 * A barra do navegador acompanha o tema que está NA TELA.
 *
 * Ela é declarada uma vez no `themeColor` do layout, em escuro, que é o padrão
 * do app. Duas coisas mudam isso depois: a rota de tema fixo (a porta é sempre
 * escura, a página externa é sempre clara) e a ESCOLHA da pessoa, que o
 * `next-themes` guarda no aparelho.
 *
 * Ela seguia o `prefers-color-scheme` enquanto o tema também seguia. Com o
 * escuro fixo como padrão, quem está com o celular no claro veria uma barra
 * branca em cima de um app preto, e quem troca para o claro no cabeçalho ficaria
 * com a barra escura.
 *
 * A cor vai numa meta NOSSA, posta antes da do Next. O Next escreve a dele de
 * novo com a cor do layout (o escuro) logo depois de a tela abrir e a cada troca
 * de rota, sem o tema mudar. Pintar a dele uma vez só, como era, deixava o
 * escuro voltar, e no tema claro a casca do iPhone punha a hora e a bateria em
 * branco por cima da página clara (até 21/10/2026). Quem lê a cor (a casca e o
 * navegador) lê a PRIMEIRA meta, então a do Next pode voltar ao escuro sem
 * ninguém ver: corrigir só a dele chegaria tarde, porque a vigia da casca é
 * avisada antes e repassaria o escuro, e a hora piscaria a cada troca de tela.
 *
 * Num efeito, e não num script que roda antes de o React assumir a página:
 * mexer na meta antes disso derruba a hidratação (o React refaz a árvore
 * inteira no cliente).
 */
function CorDaBarra({ forcado }: { forcado: "light" | "dark" | undefined }) {
  const { resolvedTheme } = useTheme()
  const tema = forcado ?? (resolvedTheme === "light" ? "light" : "dark")
  useEffect(() => {
    const cor = COR_DA_BARRA[tema]
    const pintar = () => {
      let nossa = document.head.querySelector<HTMLMetaElement>(
        'meta[name="theme-color"][data-cor-da-barra]',
      )
      if (!nossa) {
        nossa = document.createElement("meta")
        nossa.name = "theme-color"
        nossa.setAttribute("data-cor-da-barra", "")
      }
      const primeira = document.head.querySelector('meta[name="theme-color"]')
      if (primeira !== nossa) document.head.insertBefore(nossa, primeira)
      /* A do Next também leva a cor, para quem olhar todas. E só quando
         difere: escrever o mesmo valor também conta como mudança para a vigia,
         e ela se chamaria sem parar. */
      document
        .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
        .forEach((m) => {
          if (m.content !== cor) m.content = cor
        })
    }
    pintar()
    /* A vigia devolve a cor e a frente da fila: o Next reescreve o valor da meta
       dele e também troca a meta por uma nova. */
    const vigia = new MutationObserver(pintar)
    vigia.observe(document.head, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["content"],
    })
    return () => vigia.disconnect()
  }, [tema])
  return null
}

/**
 * Quem decide o tema da rota é `lib/tema/politica-de-tema.ts`.
 *
 * As listas viviam aqui dentro, e a tela não tinha como consultá-las: o `<html>`
 * ia para um tema e o `AreaTema` da própria tela ia para outro, porque o
 * `resolvedTheme` não conhece o tema forçado. Agora a política é um módulo, o
 * provider aplica no documento e o `useTemaDaTela()` responde para a tela, os
 * dois pela mesma fonte.
 */
export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  const pathname = usePathname() ?? ""
  const forcedTheme = temaForcadoPorRota(pathname)
  return (
    <NextThemesProvider {...props} forcedTheme={forcedTheme}>
      {/* Dentro do provedor, e não fora: quem pinta a barra precisa saber a
          escolha guardada, e o `useTheme` só responde de dentro. */}
      <CorDaBarra forcado={forcedTheme} />
      {children}
    </NextThemesProvider>
  )
}
